// The insight detail's LLM guidance block and its export name — both pure, both read the wire
// exactly where the backend writes it (verified against django-rag `insights`, ORP-20402):
//
//   actions → `llm_actions`                      (`models.py:353`, served by InsightDetailSerializer)
//   risks   → `llm_metadata.postprocess.risks`   (`core/step_registry.py:317` nests per LLM stage;
//                                                 schema `core/llm_step.py:19,65` = array<string>)
//
// Sparse by design: 141 of production's 1539 insights carry either, so every consumer must treat
// "nothing to show" as the common case and render no section at all.
import type { InsightResult } from '../models/insight.types'

import { toApiDate } from '@/shared/lib/date/toApiDate'
import { toSnakeCase } from '@/shared/lib/string'

export interface InsightGuidance {
  explanation: string
  actions: string[]
  risks: string[]
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** v3's `normalizeTextList` (`InsightDetailModal.vue:668-675`): array of scalars, or a lone string. */
function textList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean)
  }
  return typeof value === 'string' && value.trim() ? [value.trim()] : []
}

export function resolveInsightGuidance(insight: InsightResult | null | undefined): InsightGuidance {
  const postprocess = record(record(insight?.llm_metadata)?.postprocess)
  const explanation = insight?.llm_explanation
  return {
    explanation: typeof explanation === 'string' ? explanation.trim() : '',
    actions: textList(insight?.llm_actions),
    risks: textList(postprocess?.risks),
  }
}

export function hasInsightGuidance(guidance: InsightGuidance): boolean {
  return guidance.explanation !== '' || guidance.actions.length > 0 || guidance.risks.length > 0
}

/**
 * The PDF's base name, from the insight's config enum (v3 used the same source,
 * `composables/useInsightExport.js:11-15`).
 *
 * Only the BASE: `shared/lib/export`'s `exportPdf` snake-cases this and appends its own
 * `_{YYYY_MM_DD}_{hash}.pdf`. v3's exact `{config_enum}_{YYYY-MM-DD}.pdf` is therefore not
 * reproducible without changing the shared adapter — which also serves module 28's weekly summary,
 * and whose hash exists to stop repeat exports overwriting each other. The date is still in the
 * name, so the intent survives; the separator and the suffix differ.
 */
export function insightExportName(insight: InsightResult | null | undefined): string {
  const configEnum = typeof insight?.config_enum === 'string' ? insight.config_enum.trim() : ''
  return configEnum || 'insight'
}

export function insightCsvName(insight: InsightResult | null | undefined, today: Date): string {
  const title = typeof insight?.title === 'string' ? insight.title.trim() : ''
  return `${toSnakeCase(title || 'insight')}_${toApiDate(today)}`
}
